import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, PackageCheck } from "lucide-react";
import api, { formatPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const polls = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {}
      polls.current += 1;
      if (polls.current > 20) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 1500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="min-h-[60vh] bg-slate-50 py-16">
      <div className="max-w-2xl mx-auto px-4 lg:px-8">
        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center">
          {status === "checking" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full border-4 border-slate-100 border-t-[#0055FF] animate-spin" />
              <div className="mt-4 font-heading text-lg">Confirming your payment…</div>
              <div className="text-sm text-slate-500 mt-1">Please don&apos;t close this page.</div>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
              <h1 className="mt-4 font-heading text-3xl font-bold text-slate-900">Thank you — order confirmed</h1>
              <p className="mt-2 text-slate-600">A confirmation email is on the way. Order <span className="font-mono">{order?.order_id}</span></p>
              {order?.total !== undefined && <div className="mt-2 text-slate-900 font-heading text-xl">{formatPrice(order.total)}</div>}
              <div className="mt-8 flex items-center justify-center gap-3">
                <Button asChild><Link to="/shop">Continue shopping</Link></Button>
                <Button asChild variant="outline"><Link to="/account/orders">View my orders</Link></Button>
              </div>
            </>
          )}
          {(status === "failed" || status === "timeout" || status === "error") && (
            <>
              <PackageCheck className="w-16 h-16 mx-auto text-amber-500" />
              <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Payment status unclear</h1>
              <p className="mt-2 text-slate-600">If you were charged, your order will still be created. Please contact support with session {sessionId}.</p>
              <div className="mt-6">
                <Button asChild><Link to="/contact">Contact support</Link></Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
